import {
  FormFieldKeywords,
  ValidationKind,
  IExternalFieldDependency,
  AuthorFieldValidationOptions
} from '@publishing-engine/domain/types/article';
import {
  PHONE_NUMBER_REGEX,
  PASSWORD_REGEX,
  DATE_REGEX_STRING
} from '@publishing-engine/domain/utils';
import {
  emailRegexString,
  orcidIdOrUrlRegex
} from '@publishing-engine/domain/validation';

export type JSONSchema = {
  type:
    | 'string'
    | 'number'
    | 'integer'
    | 'object'
    | 'array'
    | 'boolean'
    | 'null';
  properties: Record<string, JSONSchema>;
  required: string[];
};

export const jsonSchemas: Record<ValidationKind['kind'], (params?: any) => {}> =
  {
    stringArray(params: FormFieldKeywords['params']['validations']) {
      return {
        type: 'array',
        items: {
          type: 'string',
          minLength: params.minLength,
          maxLength: params.maxLength
        },
        ...(params.required
          ? {
              minItems: Math.max(params.minCountOfKeywords, 1)
            }
          : {}),
        maxItems: params.maxCountOfKeywords ?? 99_999_999
      };
    },
    string(params: {
      required: boolean;
      minLength?: number;
      maxLength?: number;
      minWords?: number;
      maxWords?: number;
      externalFieldDependency?: IExternalFieldDependency;
    }) {
      const {
        required,
        maxLength,
        minLength,
        minWords,
        maxWords,
        externalFieldDependency
      } = params;
      return {
        type: required ? 'string' : ['null', 'string'],
        maxLength: maxLength ?? 99_999_999,
        ...((externalFieldDependency?.path && !required) || !required
          ? {}
          : { minLength: minLength ?? 1 }),
        pattern: '^$|.*\\S.*',
        ...(minWords !== undefined && { minWords }),
        ...(maxWords !== undefined && { maxWords })
      };
    },
    nullString({
      required,
      maxLength
    }: {
      required: boolean;
      maxLength?: number;
    }) {
      return {
        type: required ? 'string' : ['null', 'string'],
        minLength: required ? 1 : 0,
        maxLength: maxLength ?? 99_999_999
      };
    },
    bool({ value }: { value?: boolean }) {
      if (value) {
        return { type: 'boolean', const: value };
      } else {
        return { type: 'boolean' };
      }
    },
    author({
      orcidField,
      affiliationField,
      contributionsField = 'hidden',
      contributionDegreeField = 'hidden'
    }: {
      orcidField: AuthorFieldValidationOptions;
      affiliationField: AuthorFieldValidationOptions;
      contributionsField: AuthorFieldValidationOptions;
      contributionDegreeField: AuthorFieldValidationOptions;
    }) {
      return {
        type: 'object',
        properties: {
          firstName: {
            type: 'string',
            minLength: 1,
            pattern: '\\S'
          },
          lastName: {
            type: 'string',
            minLength: 1,
            pattern: '\\S'
          },
          ...(affiliationField !== 'hidden'
            ? {
                affiliation: {
                  type:
                    affiliationField === 'mandatory'
                      ? 'string'
                      : ['null', 'string'],
                  ...(affiliationField === 'mandatory' && {
                    pattern: '\\S',
                    minLength: 3
                  })
                }
              }
            : {}),
          email: {
            type: 'string',
            pattern: emailRegexString,
            minLength: 6,
            maxLength: 127
          },
          ...(orcidField !== 'hidden'
            ? {
                orcid: {
                  type:
                    orcidField === 'mandatory' ? 'string' : ['null', 'string'],
                  ...(orcidField === 'mandatory' && {
                    pattern: orcidIdOrUrlRegex.toString().slice(1, -1),
                    minLength: 3
                  })
                }
              }
            : {}),
          ringgoldId: {
            type: ['null', 'string']
          },
          ...(contributionsField !== 'hidden'
            ? {
                contributions: {
                  type:
                    contributionsField === 'mandatory'
                      ? 'array'
                      : ['array', 'null'],
                  ...(contributionsField === 'mandatory'
                    ? {
                        minItems: 1
                      }
                    : {}),
                  items: {
                    type: 'object',
                    properties: {
                      contribution: {
                        type: 'string',
                        minLength: 1
                      },
                      ...(contributionDegreeField !== 'hidden'
                        ? {
                            degree: {
                              type:
                                contributionDegreeField === 'mandatory'
                                  ? 'string'
                                  : ['null', 'string'],
                              ...(contributionDegreeField === 'mandatory' && {
                                minLength: 1
                              })
                            }
                          }
                        : {})
                    },
                    required: [
                      'contribution',
                      ...(contributionDegreeField === 'mandatory'
                        ? ['degree']
                        : [])
                    ]
                  }
                }
              }
            : {})
        },
        required: [
          'firstName',
          'lastName',
          'email',
          ...(orcidField === 'mandatory' ? ['orcid'] : []),
          ...(affiliationField === 'mandatory' ? ['affiliation'] : [])
        ]
      };
    },
    authorType({ required }: { required: boolean }) {
      return {
        type: required ? 'string' : ['null', 'string'],
        ...(required
          ? {
              pattern: emailRegexString,
              minLength: 6,
              maxLength: 127
            }
          : {})
      };
    },
    authors({
      minItems,
      orcidField,
      affiliationField,
      contributionsField
    }: {
      minItems?: number;
      orcidField: AuthorFieldValidationOptions;
      affiliationField: AuthorFieldValidationOptions;
      contributionsField: AuthorFieldValidationOptions;
    }) {
      return {
        type: 'array',
        items: jsonSchemas.author({
          orcidField,
          affiliationField,
          contributionsField
        }),
        minItems: minItems ?? 0
      };
    },
    funding(params: { required: boolean }) {
      return {
        type: params.required ? 'object' : ['null', 'object'],
        properties: {
          exists: {
            type: 'boolean'
          },
          value: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                fundingBody: {
                  type: 'string',
                  minLength: 1
                },
                grantNumber: {
                  type: 'string'
                }
              },
              required: ['fundingBody']
            }
          }
        },
        required: ['exists'],
        if: {
          properties: {
            exists: { const: true }
          }
        },
        then: {
          properties: {
            value: {
              type: 'array',
              minItems: 1
            }
          }
        }
      };
    },
    fundingGeneric(params: { required: boolean }) {
      return {
        type: params.required ? 'object' : ['null', 'object'],
        properties: {
          exists: {
            type: 'string',
            enum: ['not known', 'self funded', 'externally funded']
          },
          value: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                fundingBody: { type: 'string', minLength: 1 },
                grantNumber: { type: 'string' }
              },
              required: ['fundingBody']
            }
          }
        },
        required: ['exists'],
        if: {
          properties: {
            exists: { const: 'externally funded' }
          }
        },
        then: {
          properties: {
            value: { type: 'array', minItems: 1 }
          }
        },
        else: {
          properties: {
            value: { type: 'array', maxItems: 0 }
          }
        },
        additionalProperties: true
      };
    },
    figuresAndTables(params: {
      required: boolean;
      isSourceFileEnabled: boolean;
      isPermissionEvidenceEnabled: boolean;
      sourceFile: { required: boolean };
      permissionEvidence: { required: boolean };
    }) {
      return {
        type: params.required ? 'object' : ['null', 'object'],
        properties: {
          exists: {
            type: 'boolean'
          },
          value: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                referenceName: {
                  type: 'string',
                  minLength: 1
                },
                isReproduced: {
                  type: 'boolean',
                  default: false
                },
                ...(params.isSourceFileEnabled
                  ? { sourceFile: jsonSchemas.file(params.sourceFile.required) }
                  : {}),
                ...(params.isPermissionEvidenceEnabled
                  ? {
                      permissionEvidence: jsonSchemas.file(
                        params.permissionEvidence.required
                      )
                    }
                  : {})
              },
              required: ['referenceName', 'isReproduced']
            }
          }
        },
        required: ['exists'],
        if: {
          properties: {
            exists: { const: true }
          }
        },
        then: {
          properties: {
            value: {
              type: 'array',
              minItems: 1
            }
          }
        }
      };
    },
    file(params: { required: boolean }) {
      return {
        type: params.required ? 'object' : ['null', 'object'],
        properties: {
          referenceName: {
            type: 'string'
          },
          name: {
            type: 'string'
          },
          extension: {
            type: 'string'
          },
          url: {
            type: 'string',
            minLength: 5
          }
        },
        required: ['referenceName', 'name', 'extension', 'url']
      };
    },
    fileArray(params: { required: boolean; minItems?: number }) {
      return {
        type: params.required ? 'array' : ['null', 'array'],
        items: jsonSchemas.file({ required: true }),
        minItems: params.minItems ?? 0
      };
    },
    options(params: {
      required: boolean;
      options: string[];
      externalFieldDependency: IExternalFieldDependency;
    }) {
      const { options, externalFieldDependency } = params;
      return {
        type: params.required ? 'string' : ['string', 'null'],
        ...(externalFieldDependency?.path || !options.length || !params.required
          ? {}
          : { enum: options })
      };
    },
    publishingPreference(params: { required: boolean }) {
      return {
        type: params.required ? 'object' : ['null', 'object'],
        properties: {
          model: {
            type: 'string',
            enum: ['openAccess', 'subscription']
          },
          acknowledgeCharge: {
            type: 'boolean'
          }
        },
        if: {
          properties: {
            model: { const: 'openAccess' }
          }
        },
        then: {
          properties: {
            acknowledgeCharge: { const: true }
          }
        }
      };
    },
    openAccessOnly(params: { required: boolean }) {
      // Accept boolean from frontend, will be transformed to object in backend
      if (params.required) {
        return {
          type: 'boolean',
          const: true
        };
      }
      return {
        type: ['boolean', 'null']
      };
    },
    dataAvailabilityPreference(params: { required: boolean }) {
      return {
        type: params.required ? 'object' : ['null', 'object'],
        properties: {
          status: {
            type: 'string',
            enum: [
              'openly-available',
              'embargo-period',
              'included-in-article',
              'not-available'
            ]
          }
        },
        required: params.required ? ['status'] : []
      } as any;
    },
    dependantStringField(params: {
      options?: string[];
      required: boolean;
      dependentRequired?: boolean | { yes: boolean; no: boolean };
      dependantField: string;
      maxLength?: number;
      pattern?: string;
      labels?: { sectionNo: { title: string; explanation: string } };
    }) {
      const isRequired = params.required;
      const depReq = params.dependentRequired;
      const yesRequired =
        (typeof depReq === 'object' ? depReq.yes : depReq) ?? params.required;
      const noRequired =
        (typeof depReq === 'object' ? depReq.no : depReq) ?? params.required;
      const hasNoSection = !!params.labels?.sectionNo?.explanation;

      const makeBranch = (isRequiredBranch: boolean) => ({
        properties: {
          [params.dependantField]: {
            type: 'string',
            ...(isRequiredBranch && { pattern: params.pattern || '\\S' }),
            minLength: isRequiredBranch ? 1 : 0,
            maxLength: params.maxLength ?? 99_999_999
          }
        },
        required: isRequiredBranch ? [params.dependantField] : []
      });

      return {
        type: isRequired ? 'object' : ['null', 'object'],
        properties: {
          exists: {
            type: isRequired ? 'boolean' : ['boolean', 'null']
          },
          [params.dependantField]: {
            type: 'string',
            ...(params.options ? { enum: params.options } : {})
          }
        },
        required: ['exists'],
        allOf: [
          {
            if: {
              properties: {
                exists: { const: true }
              }
            },
            then: makeBranch(yesRequired)
          },
          ...(hasNoSection
            ? [
                {
                  if: {
                    properties: {
                      exists: { const: false }
                    }
                  },
                  then: makeBranch(noRequired)
                }
              ]
            : [])
        ]
      };
    },
    dependantCheckboxArray(params: {
      required: boolean;
      dependantField: string;
      minCountOfKeywords?: number;
      maxCountOfKeywords?: number;
    }) {
      return {
        type: params.required ? 'object' : ['null', 'object'],
        properties: {
          exists: {
            type: params.required ? 'boolean' : ['boolean', 'null']
          },
          [params.dependantField]: {
            type: 'array',
            items: {
              type: 'string'
            }
          }
        },
        required: ['exists'],
        if: {
          properties: {
            exists: { const: true }
          }
        },
        then: {
          properties: {
            [params.dependantField]: {
              type: 'array',
              items: {
                type: 'string'
              },
              ...(params.required
                ? {
                    minItems: Math.max(params.minCountOfKeywords ?? 1, 1)
                  }
                : { minItems: params.minCountOfKeywords ?? 0 }),
              maxItems: params.maxCountOfKeywords ?? 99_999_999
            }
          }
        }
      };
    },
    dependantCheckboxArrayWithText(params: {
      required: boolean;
      dependantField: string;
      textField: string;
      minCountOfKeywords?: number;
      maxCountOfKeywords?: number;
      textMaxLength?: number;
    }) {
      return {
        type: params.required ? 'object' : ['null', 'object'],
        properties: {
          exists: {
            type: params.required ? 'boolean' : ['boolean', 'null']
          },
          [params.dependantField]: {
            type: 'array',
            items: {
              type: 'string'
            }
          },
          [params.textField]: {
            type: 'string'
          }
        },
        required: ['exists'],
        if: {
          properties: {
            exists: { const: true }
          }
        },
        then: {
          properties: {
            [params.dependantField]: {
              type: 'array',
              items: {
                type: 'string'
              },
              ...(params.required
                ? {
                    minItems: Math.max(params.minCountOfKeywords ?? 1, 1)
                  }
                : { minItems: params.minCountOfKeywords ?? 0 }),
              maxItems: params.maxCountOfKeywords ?? 99_999_999
            },
            [params.textField]: {
              type: 'string',
              pattern: '\\S',
              minLength: params.required ? 1 : 0,
              maxLength: params.textMaxLength ?? 99_999_999
            }
          }
        }
      };
    },
    reviewer(params?: { requiredReason?: boolean }) {
      const isReasonRequired = params?.requiredReason ?? true;

      return {
        type: 'object',
        properties: {
          firstName: {
            type: 'string',
            minLength: 1
          },
          lastName: {
            type: 'string',
            minLength: 1
          },
          email: {
            type: 'string',
            pattern: emailRegexString,
            minLength: 6,
            maxLength: 127
          },
          affiliation: {
            type: 'string',
            minLength: 1,
            pattern: '\\S'
          },
          ringgoldId: {
            type: ['null', 'string']
          },
          country: {
            type: 'string',
            minLength: 1,
            pattern: '\\S'
          },
          reason: {
            type: isReasonRequired ? 'string' : ['string', 'null'],
            minLength: isReasonRequired ? 1 : 0
          }
        },
        required: [
          'firstName',
          'lastName',
          'email',
          'affiliation',
          'country',
          ...(isReasonRequired ? ['reason'] : [])
        ]
      };
    },
    reviewers(params: {
      required: boolean;
      minItems?: number;
      maxItems?: number;
      isOpposed?: boolean;
    }) {
      const isOpposed = params?.isOpposed ?? false;

      return {
        type: params.required ? 'object' : ['null', 'object'],
        properties: {
          exists: {
            type: 'boolean'
          },
          value: {
            type: 'array',
            items: jsonSchemas.reviewer({ requiredReason: !isOpposed }),
            minItems: params.minItems ?? 0,
            ...(params.maxItems ? { maxItems: params.maxItems } : {})
          }
        },
        required: ['exists'],
        if: {
          properties: {
            exists: { const: true }
          }
        },
        then: {
          properties: {
            value: {
              type: 'array',
              minItems: 1,
              ...(params.maxItems ? { maxItems: params.maxItems } : {})
            }
          }
        }
      };
    },
    reviewForAuthor() {
      return {
        type: 'object',
        properties: {
          comment: {
            type: 'string',
            minLength: 1
          }
        },
        required: ['comment']
      };
    },
    reviewsForAuthor(params: { minItems?: number }) {
      return {
        type: 'array',
        items: jsonSchemas.reviewForAuthor(),
        minItems: params.minItems ?? 0
      };
    },
    number(params: { min?: number; max?: number; required?: boolean }) {
      return {
        type: params.required ? 'number' : ['number', 'null'],
        minimum: params.min ?? 0,
        maximum: params.max ?? 99_999_999
      };
    },
    integer(params: { min?: number; max?: number; required?: boolean }) {
      return {
        type: params.required ? 'integer' : ['integer', 'null'],
        minimum: params.min ?? 0,
        maximum: params.max ?? 99_999_999
      };
    },
    date({ required }: { required?: boolean }) {
      return {
        type: required ? 'string' : ['string', 'null'],
        ...(required && {
          patter: DATE_REGEX_STRING
        })
      };
    },
    phoneNumber(params: {
      minLength?: number;
      maxLength?: number;
      required?: boolean;
    }) {
      return {
        type: params.required ? 'string' : ['string', 'null'],
        ...(params.required && { pattern: PHONE_NUMBER_REGEX }),
        minLength: params.minLength ?? 0,
        maxLength: params.maxLength ?? 99_999_999
      };
    },
    password(params: {
      minLength?: number;
      maxLength?: number;
      required?: boolean;
    }) {
      return {
        type: params.required ? 'string' : ['string', 'null'],
        ...(params.required && {
          // Strong password pattern
          pattern: PASSWORD_REGEX
        }),
        minLength: params.minLength ?? 8,
        maxLength: params.maxLength ?? 32
      };
    },
    customArray(params: {
      required?: boolean;
      minItems?: number;
      maxItems?: number;
      items: { properties: Record<string, any>; required?: string[] };
    }) {
      return {
        type: 'array',
        items: params.items,
        ...(params.required
          ? { minItems: Math.max(params.minItems ?? 1, 1) }
          : { minItems: params.minItems ?? 0 }),
        maxItems: params.maxItems ?? 99_999_999
      };
    },
    objectArray(params: {
      required?: boolean;
      minItems?: number;
      maxItems?: number;
    }) {
      return {
        type: params.required ? 'array' : ['array', 'null'],
        items: {
          type: 'object',
          additionalProperties: true
        },
        minItems: params.minItems ?? 0,
        maxItems: params.maxItems ?? 99_999_999
      };
    },
    targetJournals(params: {
      required: boolean;
      minItems?: number;
      maxItems?: number;
    }) {
      return {
        type: params.required ? 'array' : ['array', 'null'],
        items: {
          type: 'object',
          properties: {
            id: {
              type: 'string',
              minLength: 26,
              maxLength: 26
            },
            articleTypeCategoryId: {
              type: 'string',
              minLength: 26,
              maxLength: 26
            }
          },
          required: ['id', 'articleTypeCategoryId'],
          additionalProperties: false
        },
        minItems: params.minItems ?? 0,
        maxItems: params.maxItems ?? 99_999_999
      };
    }
  };
